import { FC } from 'react';

import { Button, Text, TextStyle, useBreakpoint, VSpacingContainer } from '@hh.ru/magritte-ui';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import useTrls from 'hooks/translation/useTrls';

import { CampaignStatusArchivedButton } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignName/components/CampaignStatusArchivedButton';
import {
    CampaignTableModerationStatus,
    getCampaignTableModerationStatus,
    getUrlToBanners,
} from 'components/CampaignsDashboard/utils/utils';

import { CampaignFrontendStatus, DashboardCampaign } from 'store/models/campaignsDashboard';

const TrlKeys = {
    detailsButton: 'CampaignsDashboard.CampaignsTable.CampaignStatus.detailsButton',
    archived: 'CampaignsDashboard.CampaignsTable.CampaignStatus.archived',
    archiveReason: 'CampaignsDashboard.CampaignsTable.CampaignStatus.archiveReason',
    blocked: 'CampaignsDashboard.CampaignsTable.CampaignStatus.blocked',
    insufficientBalance: 'CampaignsDashboard.CampaignsTable.CampaignStatus.insufficientBalance',
    budgetExceeded: 'CampaignsDashboard.CampaignsTable.CampaignStatus.budgetExceeded',
    dailyBudgetExceedStatus: 'CampaignsDashboard.CampaignsTable.CampaignStatus.dailyBudgetExceedStatus',
    moderationBanned: 'CampaignsDashboard.CampaignsTable.CampaignStatus.moderationBanned',
    onModeration: 'CampaignsDashboard.CampaignsTable.CampaignStatus.onModeration',
    partialModerationFailed: 'CampaignsDashboard.CampaignsTable.CampaignStatus.partialModerationFailed',
    active: 'CampaignsDashboard.CampaignsTable.CampaignStatus.active',
    activeWithDelayedStart: 'CampaignsDashboard.CampaignsTable.CampaignStatus.activeWithDelayedStart',
    inactiveDueToEndDate: 'CampaignsDashboard.CampaignsTable.CampaignStatus.inactiveDueToEndDate',
};

const CAMPAIGN_STATUS_MAP: Record<CampaignFrontendStatus, { style: TextStyle; trlKey: string } | null> = {
    [CampaignFrontendStatus.Archived]: { style: 'primary', trlKey: TrlKeys.archived },
    [CampaignFrontendStatus.Blocked]: { style: 'primary', trlKey: TrlKeys.blocked },
    [CampaignFrontendStatus.ActiveWithDelayedStart]: { style: 'positive', trlKey: TrlKeys.activeWithDelayedStart },
    [CampaignFrontendStatus.BudgetExceeded]: { style: 'warning', trlKey: TrlKeys.budgetExceeded },
    [CampaignFrontendStatus.DailyBudgetExceedStatus]: { style: 'warning', trlKey: TrlKeys.dailyBudgetExceedStatus },
    [CampaignFrontendStatus.Active]: { style: 'positive', trlKey: TrlKeys.active },
    [CampaignFrontendStatus.InactiveDueToEndDate]: { style: 'primary', trlKey: TrlKeys.inactiveDueToEndDate },
    [CampaignFrontendStatus.Unknown]: null,
};

const MODERATION_STATUS_MAP: Record<CampaignTableModerationStatus, { style: TextStyle; trlKey: string } | null> = {
    [CampaignTableModerationStatus.InProgress]: { style: 'primary', trlKey: TrlKeys.onModeration },
    [CampaignTableModerationStatus.Rejected]: { style: 'negative', trlKey: TrlKeys.moderationBanned },
    [CampaignTableModerationStatus.PartialFailed]: { style: 'warning', trlKey: TrlKeys.partialModerationFailed },
    [CampaignTableModerationStatus.None]: null,
};

export const CampaignStatus: FC<{ campaign: DashboardCampaign; isInsufficientBalance: boolean }> = ({
    campaign,
    isInsufficientBalance,
}) => {
    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const { id: campaignId, frontendStatus, banners, productType } = campaign;

    const goToBannersButtonLink = (
        <Button
            mode="secondary"
            style="neutral"
            size="small"
            stretched={isMobile}
            Element={SPALink}
            to={getUrlToBanners(campaignId)}
            data-qa={`campaign-status-banners-link-${campaignId}`}
        >
            {trls(TrlKeys.detailsButton)}
        </Button>
    );

    const moderationTableStatus = getCampaignTableModerationStatus(banners, productType, frontendStatus);
    const isModerationRejected = moderationTableStatus === CampaignTableModerationStatus.Rejected;

    let campaignStatus = CAMPAIGN_STATUS_MAP[frontendStatus];

    if (isInsufficientBalance) {
        campaignStatus = { style: 'negative', trlKey: TrlKeys.insufficientBalance };
    }
    const isStatusVisible =
        ([CampaignFrontendStatus.Archived, CampaignFrontendStatus.Blocked].includes(frontendStatus) &&
            isModerationRejected) ||
        !isModerationRejected ||
        campaignStatus;

    return (
        <>
            {isStatusVisible && (
                <VSpacingContainer default={8}>
                    <Text typography="label-3-regular" style={campaignStatus?.style}>
                        {trls(campaignStatus?.trlKey)}
                    </Text>
                    {frontendStatus === CampaignFrontendStatus.Archived && (
                        <CampaignStatusArchivedButton campaign={campaign} />
                    )}
                </VSpacingContainer>
            )}
            {MODERATION_STATUS_MAP[moderationTableStatus] && (
                <VSpacingContainer default={isMobile ? 12 : 8}>
                    <Text typography="label-3-regular" style={MODERATION_STATUS_MAP[moderationTableStatus]?.style}>
                        {trls(MODERATION_STATUS_MAP[moderationTableStatus]?.trlKey)}
                    </Text>
                    {[CampaignTableModerationStatus.Rejected, CampaignTableModerationStatus.PartialFailed].includes(
                        moderationTableStatus
                    ) && goToBannersButtonLink}
                </VSpacingContainer>
            )}
        </>
    );
};
